import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';

import '../../../../CallHistory/services/call-history.service';
import {
  ActiveCallDetail,
  RING_CENTRAL_EVENTS,
  RING_CENTRAL_FRAME_ID,
  getRingCentralIntegrationSnapshot,
  initRingCentralMessageListener,
  ensureSoftphoneOnDialer,
  onRingCentralFrameLoaded,
  cancelSoftphoneNavigation,
} from '../../../../../utils/ringCentralDialer';

import RingCentralQuickDial, {
  RingCentralConnectionStatus,
} from './RingCentralQuickDial';
import IncomingCallWidget from './IncomingCallWidget';
import {
  buildRingCentralSoftphoneUrl,
  isRingCentralConfigured,
  isRingCentralEnabled,
} from './ringCentralConfig';

import './RingCentralIntegration.css';

type StatusDetail = {
  loggedIn?: boolean;
  dialerReady?: boolean;
  webphoneConnected?: boolean;
};

const RC_OUTSIDE_CLICK_IGNORE = '.pf-rc-incoming-call, .pf-rc-active-call-chip';

const RingCentralIntegration = () => {
  const toast = useRef<Toast>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const [widgetCollapsed, setWidgetCollapsed] = useState(true);
  const [shouldMountFrame, setShouldMountFrame] = useState(false);
  const [status, setStatus] = useState<StatusDetail>({});
  const [activeCall, setActiveCall] = useState<ActiveCallDetail | null>(null);
  const [rcEnabled, setRcEnabled] = useState(() => isRingCentralEnabled());

  useEffect(() => {
    const syncRcEnabled = () => setRcEnabled(isRingCentralEnabled());
    window.addEventListener('pf:client-details-updated', syncRcEnabled);
    return () =>
      window.removeEventListener('pf:client-details-updated', syncRcEnabled);
  }, []);

  const isConfigured = isRingCentralConfigured();
  const softphoneUrl = useMemo(() => buildRingCentralSoftphoneUrl(), []);

  const connectionStatus: RingCentralConnectionStatus = useMemo(() => {
    if (status.loggedIn && status.webphoneConnected) return 'connected';
    if (status.loggedIn || status.dialerReady) return 'connecting';
    return 'offline';
  }, [status]);

  const syncStatus = useCallback(() => {
    const snap = getRingCentralIntegrationSnapshot();
    setStatus({
      loggedIn: snap.loggedIn,
      dialerReady: snap.dialerReady,
      webphoneConnected: snap.webphoneConnected,
    });
    setActiveCall(snap.activeCall);
  }, []);

  const openPhonePanel = useCallback(
    (options?: { goToDialer?: boolean }) => {
      setShouldMountFrame(true);
      setWidgetCollapsed(false);
      if (options?.goToDialer && !activeCall) {
        ensureSoftphoneOnDialer();
      } else {
        cancelSoftphoneNavigation();
      }
    },
    [activeCall]
  );

  const handleToggleCollapse = useCallback(() => {
    if (widgetCollapsed) {
      openPhonePanel();
    } else {
      setWidgetCollapsed(true);
    }
  }, [openPhonePanel, widgetCollapsed]);

  useEffect(() => {
    if (widgetCollapsed) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (dockRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest(RC_OUTSIDE_CLICK_IGNORE)
      ) {
        return;
      }
      setWidgetCollapsed(true);
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [widgetCollapsed]);

  useEffect(() => {
    if (!rcEnabled) return undefined;

    initRingCentralMessageListener();
    setShouldMountFrame(true);
    syncStatus();

    const onStatus = (e: Event) => {
      const detail = (e as CustomEvent<StatusDetail>).detail;
      setStatus({
        loggedIn: detail?.loggedIn,
        dialerReady: detail?.dialerReady,
        webphoneConnected: detail?.webphoneConnected,
      });
    };

    const onOpen = () => openPhonePanel();
    const onDial = () => openPhonePanel();

    const onDialFailed = (event: Event) => {
      const detail = (
        event as CustomEvent<{ summary?: string; detail?: string }>
      ).detail;
      toast.current?.show({
        severity: 'error',
        summary: detail?.summary ?? 'Call failed',
        detail: detail?.detail ?? 'Could not make the call. Please try again.',
        life: 5000,
      });
    };

    const onCallEnd = () => {
      setActiveCall(null);
    };

    const onActiveCall = (event: Event) => {
      const detail = (event as CustomEvent<ActiveCallDetail | null>).detail;
      setActiveCall(detail ?? null);
    };

    window.addEventListener(RING_CENTRAL_EVENTS.STATUS, onStatus);
    window.addEventListener(RING_CENTRAL_EVENTS.OPEN, onOpen);
    window.addEventListener(RING_CENTRAL_EVENTS.DIAL, onDial);
    window.addEventListener(RING_CENTRAL_EVENTS.DIAL_FAILED, onDialFailed);
    window.addEventListener(RING_CENTRAL_EVENTS.CALL_END, onCallEnd);
    window.addEventListener(RING_CENTRAL_EVENTS.ACTIVE_CALL, onActiveCall);

    return () => {
      window.removeEventListener(RING_CENTRAL_EVENTS.STATUS, onStatus);
      window.removeEventListener(RING_CENTRAL_EVENTS.OPEN, onOpen);
      window.removeEventListener(RING_CENTRAL_EVENTS.DIAL, onDial);
      window.removeEventListener(RING_CENTRAL_EVENTS.DIAL_FAILED, onDialFailed);
      window.removeEventListener(RING_CENTRAL_EVENTS.CALL_END, onCallEnd);
      window.removeEventListener(RING_CENTRAL_EVENTS.ACTIVE_CALL, onActiveCall);
    };
  }, [rcEnabled, isConfigured, openPhonePanel, syncStatus]);

  const handleFrameLoad = () => {
    onRingCentralFrameLoaded();
    syncStatus();
  };

  if (!rcEnabled) {
    return null;
  }

  return (
    <>
      <Toast ref={toast} />

      <IncomingCallWidget
        rcPanelExpanded={!widgetCollapsed}
        onOpenPhonePanel={openPhonePanel}
      />

      <div
        ref={dockRef}
        className={`pf-rc-dock ${widgetCollapsed ? 'pf-rc-dock--collapsed' : 'pf-rc-dock--expanded'}${activeCall ? ' pf-rc-dock--on-call' : ''}`}
      >
        <RingCentralQuickDial
          connectionStatus={connectionStatus}
          activeCall={activeCall}
          collapsed={widgetCollapsed}
          showFab={false}
          onToggleCollapse={handleToggleCollapse}
        />

        {shouldMountFrame && isConfigured && softphoneUrl && (
          <div className="pf-rc-dock__frame-wrap">
            <iframe
              key={`rc-softphone`}
              id={RING_CENTRAL_FRAME_ID}
              title="RingCentral dial pad"
              src={softphoneUrl}
              className="pf-rc-dock__frame"
              allow="autoplay; microphone"
              scrolling="no"
              onLoad={handleFrameLoad}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default RingCentralIntegration;
